import { getDistinctColor, numCompare, toPercentageString, toStringSmartly } from '@common/Common';
import CorporateNavigation from "@common/Components/CorporateNavigation.vue";
import { KendoChart, KendoChartDataSerie, KendoChartOptions } from '@Kendo/Components/KendoChart';
import ScenarioNavigation from '@Quest/CorporateScenarios/Components/ScenarioNavigation.vue';
import { OptimizeCorporateScenarioScheduleCommandV1 } from '@Quest/CorporateScenarios/Domain/commands-core';
import CorporateScenariosCommands from '@Quest/CorporateScenarios/Domain/commands-vue-component';
import CorporateScenariosQueries from '@Quest/CorporateScenarios/Domain/queries-vue-component';
import { PortfolioReadModel, ProjectsPageReadModel, SelectionProjectReadModel } from '@Quest/CorporateScenarios/Domain/readmodels';
import { ICommandBase } from "@Quest/cqrs";
import Vue from "vue";
import Component, { mixins } from 'vue-class-component';


@Component({ components: { CorporateNavigation, ScenarioNavigation}})
export default class ProjectsPage extends mixins(CorporateScenariosCommands, CorporateScenariosQueries)
{
    get workspaceId(): number { return Number.parseInt(this.$route.params.workspaceId); }
    get strategyId() { return this.$route.params.strategyId; }
    get scenarioId() { return this.$route.params.scenarioId; }

    rm: ProjectsPageReadModel | null = null;
    searchPhrase: string | null = null;
    EfficientFrontier: EfficientFrontierChart = new EfficientFrontierChart();
    PrioritizationMatrix: PrioritizationMatrixChart = new PrioritizationMatrixChart();

    get projectsView(): SelectionProjectReadModel[]
    {
        let view = new Array(...this.rm?.Projects ?? []);

        if (this.searchPhrase)
        {
            const phrase = this.searchPhrase.toLowerCase();
            view = view.filter(a => a.Name.toLowerCase().indexOf(phrase) > -1);
        }

        return view;
    }

    fmtNum(val: number | null) { return toStringSmartly(val); }

    fmtPct(val: number | null) { return toPercentageString(val); }


    mounted()
    {
        document.title = "Enterprise Plan - Projects";
        this.runProjectsPageQueryV1(this.workspaceId, this.strategyId, this.scenarioId);
    }

    onSetModel(model: any, modelName: string)
    {
        if (modelName == "ProjectsPageReadModel")
        {
            this.rm = model as ProjectsPageReadModel;
            this.rm.Portfolios.forEach((p, index) => p.Color = getDistinctColor(index));
            this.EfficientFrontier.SetData(this.rm.Projects, this.rm.CurrencyLabel, this.rm.Portfolios);
            this.PrioritizationMatrix.SetData(this.rm.Projects, this.rm.CurrencyLabel, this.rm.Portfolios);

            Vue.nextTick(() =>
            {
                this.EfficientFrontier.Render();
                this.PrioritizationMatrix.Render();
            })
        }
    }

    onCommandSucceed(cmd: ICommandBase, correlationId: string, result: any)
    {
        if (OptimizeCorporateScenarioScheduleCommandV1.matchType(cmd))
        {
            this.runProjectsPageQueryV1(this.workspaceId, this.strategyId, this.scenarioId);
        }
    }
}



class EfficientFrontierChart
{
    isRendered: boolean = false;

    //Title: string = null;
    Chart: KendoChart;

    constructor()
    {
        const options = new KendoChartOptions();
        options.HideLegend();
        options.SetPannable(true);
        options.SetZoomable(true);
        options.SetTooltip(false, '#= dataItem.Name #');
        //options.SetOnSeriesHover(this.page.Highlight);
        //options.SetOnSeriesClick(this.page.OnSeriesClick);

        const chart = KendoChart.CreateScatterChart(options);
        this.Chart = chart;
    }

    SetData(projects: SelectionProjectReadModel[], currencyLabel: string, portfolios: PortfolioReadModel[])
    {
        let dataItems: { Id: string, Name: string, PortfolioId: string, PortfolioName: string, X: number, Y: number, Z: number, TooltipHtml: string }[] = [];

        for (const p of projects)
        {
            const xValue = p.TotalCost;
            const yValue = p.TotalValue;
            const zValue = p.CostOfValuePoint;

            dataItems.push({
                Id: p.Id,
                Name: p.Name,
                X: xValue as number,
                Y: yValue as number,
                Z: zValue as number,
                PortfolioId: p.PortfolioId,
                PortfolioName: p.PortfolioName,
                TooltipHtml: `${p.Name}`
            });
        }

        dataItems = dataItems.filter(a => a.X != null && a.Y != null && a.Z != null).sort((a, b) => numCompare(a.Z, b.Z));

        for (let i = 1; i < dataItems.length; i++) //from 1 to skip first
        {
            dataItems[i].X += dataItems[i - 1].X;
            dataItems[i].Y += dataItems[i - 1].Y;
        }

        const series: KendoChartDataSerie[] = portfolios.map(portfolio =>
        {
            const serie = new KendoChartDataSerie();
            serie.SetXField("X");
            serie.SetYField("Y");
            serie.SetCategoryField("Name");
            serie.SetColor(portfolio.Color);
            serie.SetMarkers(true, "circle"); // circle, square, triangle, cross
            serie.SetData(dataItems.filter(a => a.PortfolioId === portfolio.Id));
            return serie;
        });

        const options = this.Chart.GetOptions();
        options.SetTooltip(true, "#= dataItem.TooltipHtml #");

        const xMaxValue = Math.max(...dataItems.map(v => v.X));
        const yMaxValue = Math.max(...dataItems.map(v => v.Y));

        const xAxisMin = 0;
        const xAxisMax = xMaxValue * 1.1;
        const yAxisMin = 0;
        const yAxisMax = yMaxValue * 1.1;

        const xAxisLabelFormat = xMaxValue > 10 ? "{0:n0}" : null;
        const yAxisLabelFormat = yMaxValue > 10 ? "{0:n0}" : null;

        const xCrosshairTooltipTemplate = xMaxValue ? (e: any): string => kendo.toString(e.value / xMaxValue, "p0") : undefined;
        const yCrosshairTooltipTemplate = yMaxValue ? (e: any): string => kendo.toString(e.value / yMaxValue, "p0") : undefined;

        options.SetXAxis(null, xAxisMin, xAxisMax, `Cost (${currencyLabel})`, xAxisLabelFormat, 2, true, true, null, xCrosshairTooltipTemplate);
        options.SetYAxis(null, yAxisMin, yAxisMax, "Value", yAxisLabelFormat, true, true, null, yCrosshairTooltipTemplate);

        this.Chart.SetOptions(options);
        this.Chart.SetDataSeries(series);

        this.Chart.UpdateOptions();
    }

    Render()
    {
        if (this.isRendered === false)
        {
            this.Chart.Render1("EfficientFrontier");
            this.isRendered = true;
        }
    }
}

class PrioritizationMatrixChart
{
    isRendered: boolean = false;

    //Title: string = null;
    Chart: KendoChart;

    constructor()
    {
        const options = new KendoChartOptions();
        options.HideLegend();
        options.SetPannable(true);
        options.SetZoomable(true);
        options.SetTooltip(false, '#= dataItem.Name #');
        //options.SetOnSeriesHover(this.page.Highlight);
        //options.SetOnSeriesClick(this.page.OnSeriesClick);

        const chart = KendoChart.CreateScatterChart(options);
        this.Chart = chart;
    }

    SetData(projects: SelectionProjectReadModel[], currencyLabel: string, portfolios: PortfolioReadModel[])
    {
        let dataItems: { Id: string, Name: string, PortfolioId: string, PortfolioName: string, X: number, Y: number, TooltipHtml: string }[] = [];

        for (const p of projects)
        {
            const xValue = p.TotalCost;
            const yValue = p.TotalValue;

            dataItems.push({
                Id: p.Id,
                Name: p.Name,
                X: xValue as number,
                Y: yValue as number,
                PortfolioId: p.PortfolioId,
                PortfolioName: p.PortfolioName,
                TooltipHtml: `${p.Name}`
            });
        }

        dataItems = dataItems.filter(a => a.X != null && a.Y != null);

        const series: KendoChartDataSerie[] = portfolios.map(portfolio =>
        {
            const serie = new KendoChartDataSerie();
            serie.SetXField("X");
            serie.SetYField("Y");
            serie.SetCategoryField("Name");
            serie.SetColor(portfolio.Color);
            serie.SetMarkers(true, "circle"); // circle, square, triangle, cross
            serie.SetData(dataItems.filter(a => a.PortfolioId === portfolio.Id));
            return serie;
        });

        const options = this.Chart.GetOptions();
        options.SetTooltip(true, "#= dataItem.TooltipHtml #");

        const xMaxValue = Math.max(...dataItems.map(v => v.X));
        const yMaxValue = Math.max(...dataItems.map(v => v.Y));

        const xAxisMin = 0;
        const xAxisMax = xMaxValue * 1.1;
        const yAxisMin = 0;
        const yAxisMax = yMaxValue * 1.1;

        const xAxisLabelFormat = xMaxValue > 10 ? "{0:n0}" : null;
        const yAxisLabelFormat = yMaxValue > 10 ? "{0:n0}" : null;

        options.SetXAxis(null, xAxisMin, xAxisMax, `Cost (${currencyLabel})`, xAxisLabelFormat, 2, true, false, null);
        options.SetYAxis(null, yAxisMin, yAxisMax, "Value", yAxisLabelFormat, true, false, null);

        this.Chart.SetOptions(options);
        this.Chart.SetDataSeries(series);

        this.Chart.UpdateOptions();
    }

    Render()
    {
        if (this.isRendered === false)
        {
            this.Chart.Render1("PrioritizationMatrix");
            this.isRendered = true;
        }
    }
}
